import { useState } from 'react';
import { useSignUp } from "@clerk/clerk-react";
import { useNavigate, Link, Navigate } from 'react-router-dom';
import graduation from "../assets/images/graduation.jpg";
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useCurrentUser } from '../UserProvider/UserProvider';
import Loader from '../components/Loader';

export default function Register() {
    const { isLoaded, signUp, setActive } = useSignUp();
    const { loggedIn, appLoading } = useCurrentUser();
    const navigate = useNavigate();

    const [emailAddress, setEmailAddress] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [code, setCode] = useState("");
    const [error, setError] = useState(null);
    const [isSigningUp, setIsSigningUp] = useState(false);
    const [role, setRole] = useState("student");

    // If already logged in, redirect
    if (loggedIn) {
        return <Navigate to="/" replace />;
    }

    if (appLoading || !isLoaded) {
        return <Loader text="Loading..." />;
    }

    const handleGoogleSignUp = async () => {
        try {
            await signUp.authenticateWithRedirect({
                strategy: "oauth_google",
                redirectUrl: window.location.origin + "/sso-callback",
                redirectUrlComplete: window.location.origin + "/"
            });
        } catch (err) {
            console.error("Google sign up error:", err);
            setError("Failed to initiate Google sign up. Please try again.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isLoaded) return;
        setIsSigningUp(true);
        setError(null);

        try {
            await signUp.create({
                emailAddress: emailAddress.trim(),
                password,
                username: username.trim(),
                unsafeMetadata: { role }
            });

            // Start email verification
            await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
            setVerifying(true);
        } catch (err) {
            console.error("Sign up error:", err);
            // Handle specific Clerk errors
            if (err.errors?.[0]?.code === "form_captcha_invalid") {
                setError("Please complete the human verification below.");
            } else {
                setError(err.errors?.[0]?.message || "Registration failed. Please try again.");
            }
        } finally {
            setIsSigningUp(false);
        }
    };

    const handleVerification = async (e) => {
        e.preventDefault();
        if (!isLoaded) return;
        setIsSigningUp(true);
        setError(null);

        try {
            const completeSignUp = await signUp.attemptEmailAddressVerification({
                code,
            });

            if (completeSignUp.status === "complete") {
                await setActive({ session: completeSignUp.createdSessionId });
                navigate('/');
            } else {
                setError("Verification incomplete. Please follow the instructions.");
            }
        } catch (err) {
            console.error("Verification error:", err);
            setError(err.errors?.[0]?.message || "Verification failed. Please check the code.");
        } finally {
            setIsSigningUp(false);
        }
    };

    return (
        <div className="py-10 bg-[#17717d] min-h-screen flex items-center justify-center p-4">
            <div className="flex bg-white rounded-3xl shadow-2xl mx-auto max-w-lg lg:max-w-4xl w-full overflow-hidden h-auto my-8 transition-all">
                <div
                    className="hidden lg:block lg:w-1/2 bg-cover bg-center rounded-l-lg"
                    style={{ backgroundImage: `url(${graduation})` }}
                />

                <div className="w-full p-8 sm:p-12 lg:w-1/2 overflow-y-auto flex flex-col justify-center">
                    <div className="flex flex-col items-center mb-10">
                        <div className="bg-[#A855F7] p-5 rounded-[2.5rem] shadow-xl mb-4 transform hover:scale-105 transition-transform flex items-center justify-center">
                            <img src="/wormie-logo.svg" alt="" className="h-10 w-10" />
                        </div>
                        <h1 className="text-4xl font-black text-gray-900 tracking-tight text-center">Worm</h1>
                        <p className="text-[#17717d] font-bold mt-2 uppercase tracking-[0.3em] text-[10px] bg-[#17717d]/10 px-4 py-1.5 rounded-full">
                            {verifying ? "Verify Email" : "Create Account"}
                        </p>
                    </div>

                    {verifying && (
                         <div className="flex flex-col items-center mb-6">
                            <p className="text-gray-500 text-sm">Check your inbox for a code</p>
                         </div>
                    )}

                    {error && (
                        <div className="p-4 mt-4 text-sm text-red-800 rounded-lg bg-red-50 border border-red-200" role="alert">
                            {error}
                        </div>
                    )}

                    {!verifying ? (
                        <>
                            <button
                                onClick={handleGoogleSignUp}
                                className="flex items-center justify-center mt-4 w-full bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors cursor-pointer"
                                type="button"
                            >
                                <div className="px-4 py-3">
                                    <svg className="h-6 w-6" viewBox="0 0 40 40">
                                        <path d="M36.3425 16.7358H35V16.6667H20V23.3333H29.4192C28.045 27.2142 24.3525 30 20 30C14.4775 30 10 25.5225 10 20C10 14.4775 14.4775 9.99999 20 9.99999C22.5492 9.99999 24.8683 10.9617 26.6342 12.5325L31.3483 7.81833C28.3717 5.04416 24.39 3.33333 20 3.33333C10.7958 3.33333 3.33335 10.7958 3.33335 20C3.33335 29.2042 10.7958 36.6667 20 36.6667C29.2042 36.6667 36.6667 29.2042 36.6667 20C36.6667 18.8825 36.5517 17.7917 36.3425 16.7358Z" fill="#FFC107" />
                                        <path d="M5.25497 12.2425L10.7308 16.2583C12.2125 12.59 15.8008 9.99999 20 9.99999C22.5491 9.99999 24.8683 10.9617 26.6341 12.5325L31.3483 7.81833C28.3716 5.04416 24.39 3.33333 20 3.33333C13.5983 3.33333 8.04663 6.94749 5.25497 12.2425Z" fill="#FF3D00" />
                                        <path d="M20 36.6667C24.305 36.6667 28.2167 35.0192 31.1742 32.34L26.0159 27.975C24.3425 29.2425 22.2625 30 20 30C15.665 30 11.9842 27.2359 10.5975 23.3784L5.16254 27.5659C7.92087 32.9634 13.5225 36.6667 20 36.6667Z" fill="#4CAF50" />
                                        <path d="M36.3425 16.7358H35V16.6667H20V23.3333H29.4192C28.7592 25.1975 27.56 26.805 26.0133 27.9758C26.0142 27.975 26.015 27.975 26.0158 27.9742L31.1742 32.3392C30.8092 32.6708 36.6667 28.3333 36.6667 20C36.6667 18.8825 36.5517 17.7917 36.3425 16.7358Z" fill="#1976D2" />
                                    </svg>
                                </div>
                                <span className="px-4 py-3 text-gray-600 font-bold">Sign up with Google</span>
                            </button>

                            <div className="mt-4 flex items-center justify-between">
                                <span className="border-b w-1/5 lg:w-1/4"></span>
                                <span className="text-xs text-center text-gray-500 uppercase">or sign up with email</span>
                                <span className="border-b w-1/5 lg:w-1/4"></span>
                            </div>

                            <div className="mt-8">
                                <label className="block text-gray-400 text-[10px] font-black mb-4 text-center uppercase tracking-[0.2em]">Select Your Account Type</label>
                                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setRole("student")}
                                        className={`py-4 px-1 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 group ${role === 'student' 
                                            ? 'border-purple bg-purple/5 text-purple shadow-lg shadow-purple-500/10' 
                                            : 'border-gray-50 text-gray-300 hover:border-gray-200 hover:text-gray-400'}`}
                                    >
                                        <div className={`p-2 rounded-xl transition-colors ${role === 'student' ? 'bg-purple/10' : 'bg-gray-50 group-hover:bg-gray-100'}`}>
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" /></svg>
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-tighter">Student</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setRole("advisor")}
                                        className={`py-4 px-1 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 group ${role === 'advisor' 
                                            ? 'border-[#17717d] bg-[#17717d]/5 text-[#17717d] shadow-lg shadow-teal-500/10' 
                                            : 'border-gray-50 text-gray-300 hover:border-gray-200 hover:text-gray-400'}`}
                                    >
                                        <div className={`p-2 rounded-xl transition-colors ${role === 'advisor' ? 'bg-[#17717d]/10' : 'bg-gray-50 group-hover:bg-gray-100'}`}>
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-tighter">Advisor</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setRole("college_staff")}
                                        className={`py-4 px-1 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 group ${role === 'college_staff' 
                                            ? 'border-amber-500 bg-amber-50 text-amber-600 shadow-lg shadow-amber-500/10' 
                                            : 'border-gray-50 text-gray-300 hover:border-gray-200 hover:text-gray-400'}`}
                                    >
                                        <div className={`p-2 rounded-xl transition-colors ${role === 'college_staff' ? 'bg-amber-100' : 'bg-gray-50 group-hover:bg-gray-100'}`}>
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-tighter leading-none text-center">College Rep</span>
                                    </button>
                                </div>
                            </div>

                            <form className="mt-6" onSubmit={handleSubmit}>
                                <div className="mt-4">
                                    <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">Email Address</label>
                                    <input
                                        id="email"
                                        className="bg-gray-200 text-gray-700 focus:outline-none focus:shadow-outline border border-gray-300 rounded py-2 px-4 block w-full appearance-none transition-all"
                                        type="email"
                                        name="email"
                                        autoComplete="email"
                                        value={emailAddress}
                                        onChange={(e) => setEmailAddress(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="mt-4">
                                    <label htmlFor="username" className="block text-gray-700 text-sm font-bold mb-2">Username</label>
                                    <input
                                        id="username"
                                        className="bg-gray-200 text-gray-700 focus:outline-none focus:shadow-outline border border-gray-300 rounded py-2 px-4 block w-full appearance-none transition-all"
                                        type="text"
                                        name="username"
                                        autoComplete="username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="mt-4">
                                    <label htmlFor="password" className="block text-gray-700 text-sm font-bold mb-2">Password</label>
                                    <div className="relative">
                                        <input
                                            id="password"
                                            className="bg-gray-200 text-gray-700 focus:outline-none focus:shadow-outline border border-gray-300 rounded py-2 px-4 block w-full appearance-none transition-all pr-12"
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeSlashIcon className="h-5 w-5" />
                                            ) : (
                                                <EyeIcon className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-8">
                                    <button
                                        type="submit"
                                        className="bg-[#17717d] text-white font-bold py-2 px-4 w-full rounded hover:bg-[#135f69] transition duration-150 shadow-md"
                                        disabled={isSigningUp}
                                    >
                                        {isSigningUp ? "Processing..." : "Sign Up"}
                                    </button>
                                </div>
                                <div id="clerk-captcha" className="mt-4 flex justify-center min-h-[65px]"></div>
                            </form>
                            <div className="mt-4 flex items-center justify-between">
                                <span className="border-b w-1/5 md:w-1/4"></span>
                                <Link to="/login" className="text-xs text-[#17717d] hover:text-[#135f69] font-semibold uppercase">or login</Link>
                                <span className="border-b w-1/5 md:w-1/4"></span>
                            </div>
                        </>
                    ) : (
                        <form className="mt-6" onSubmit={handleVerification}>
                            <p className="mb-4 text-sm text-gray-600">
                                We've sent a verification code to <strong>{emailAddress}</strong>. Please enter it below.
                            </p>
                            <div className="mt-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">Verification Code</label>
                                <input
                                    className="bg-gray-200 text-gray-700 focus:outline-none focus:shadow-outline border border-gray-300 rounded py-2 px-4 block w-full appearance-none text-center tracking-widest text-lg font-mono"
                                    type="text"
                                    value={code}
                                    placeholder="######"
                                    onChange={(e) => setCode(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="mt-8">
                                <button
                                    type="submit"
                                    className="bg-[#17717d] text-white font-bold py-2 px-4 w-full rounded hover:bg-[#135f69] transition duration-150"
                                    disabled={isSigningUp}
                                >
                                    {isSigningUp ? "Verifying..." : "Verify & Login"}
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => setVerifying(false)}
                                className="mt-4 w-full text-sm text-[#17717d] hover:text-[#135f69] text-center font-medium"
                            >
                                Back to sign up
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}